// Describes which billing fields changed, for the confirm step before a billing save and for the
// company note recorded automatically whenever billing settings are saved.
import { formatDate, formatNumber, humanizeEnum } from './format';
import { planLabel } from '../components/PlanFilter';

const toDateOnly = (value) => {
  if (!value) return '';
  const str = String(value);
  return str.length >= 10 ? str.substring(0, 10) : str;
};

const NONE = 'None';
const yesNo = (value) => (value ? 'Yes' : 'No');
const orNone = (value) => (value === '' || value == null ? NONE : value);
// A plain date such as "2026-08-01" is read as local midnight, so it never shifts a day
const plainDate = (value) => (value ? formatDate(`${value}T00:00:00`, value) : NONE);
const frequency = (value) => {
  if (!value) return NONE;
  const upper = String(value).toUpperCase();
  return ['ANNUAL', 'ANNUALLY', 'YEARLY'].includes(upper) ? 'Annual' : humanizeEnum(value);
};

// The fields shown in the BillingManagement panel, with how to normalise (for comparison) and
// format (for display) each one.
const BILLING_FIELDS = [
  { key: 'accountType', label: 'Plan', normalize: (v) => v || '', format: (v) => (v ? planLabel(v) : NONE) },
  { key: 'paymentFrequency', label: 'Billing frequency', normalize: (v) => v || '', format: frequency },
  { key: 'renewalDate', label: 'Renewal date', normalize: toDateOnly, format: plainDate },
  { key: 'preferredPointOfContact', label: 'Preferred point of contact', normalize: (v) => (v || '').trim(), format: orNone },
  { key: 'freeUsers', label: 'Free seats', normalize: (v) => Number(v) || 0, format: (v) => formatNumber(v) },
  { key: 'paidUsers', label: 'Paid seats', normalize: (v) => Number(v) || 0, format: (v) => formatNumber(v) },
  { key: 'overduePayment', label: 'Overdue payment', normalize: (v) => !!v, format: yesNo },
  { key: 'usesStripe', label: 'Using Stripe', normalize: (v) => !!v, format: yesNo },
];

/**
 * Each billing field that differs between the saved company and the edited values, formatted
 * for reading: [{ key, label, from, to }], empty when nothing changed.
 *
 * @param {object} original - the company as last loaded from the server
 * @param {object} edited - the edited billing form values
 */
export function describeBillingChanges(original, edited) {
  const changes = [];
  for (const field of BILLING_FIELDS) {
    const oldValue = field.normalize(original ? original[field.key] : undefined);
    const newValue = field.normalize(edited ? edited[field.key] : undefined);
    if (oldValue !== newValue) {
      changes.push({ key: field.key, label: field.label, from: field.format(oldValue), to: field.format(newValue) });
    }
  }
  return changes;
}

/**
 * Compares the original company against the edited billing values and returns a note string
 * describing each changed field ("Billing updated - Plan: Standard to Premium; Paid seats: 50 to 60"),
 * or null if nothing changed.
 *
 * @param {object} original - the company as last loaded from the server
 * @param {object} edited - the edited billing form values
 * @returns {string|null}
 */
export function buildBillingChangeNote(original, edited) {
  const changes = describeBillingChanges(original, edited);
  if (changes.length === 0) {
    return null;
  }
  return `Billing updated - ${changes.map((change) => `${change.label}: ${change.from} to ${change.to}`).join('; ')}`;
}
